import { TranscoderJobViewModel } from '../transcoder-job-view.model';
import { IMediaTranscoderJobsListViewModelApi } from '../transcoder-job.view-model.api';
import { EMediaTranscodingItemStatus } from '../../../../domain/media-transcoder/transcoding-item';
import { ActiveTranscoderJobViewModel } from './active-transcoder-job-view.model';

export interface ItemStatusFilterOption {
  value: EMediaTranscodingItemStatus | null;
  label: string;
}

export interface GroupedMediaJob {
  baseName: string;
  jobCount: number;
  jobs: TranscoderJobViewModel[];
}

/** Active jobs only surface in-progress item statuses (Pending through Running). */
const ACTIVE_ITEM_STATUS_FILTER_MAX = EMediaTranscodingItemStatus.Running;

export class ActiveMediaTranscoderJobsViewModel {
  allJobs: ActiveTranscoderJobViewModel[] = [];
  selectedItemStatusFilter: EMediaTranscodingItemStatus | null = null;
  nameFilter = '';
  isGroupedJobsCollapsed = false;
  readonly statusFilterOptions: ItemStatusFilterOption[];

  constructor(private _api: IMediaTranscoderJobsListViewModelApi) {
    this.statusFilterOptions = [
      { value: null, label: 'All' },
      ...Object.keys(EMediaTranscodingItemStatus)
        .filter((key) => isNaN(Number(key)))
        .map((key) => ({
          value:
            EMediaTranscodingItemStatus[
              key as keyof typeof EMediaTranscodingItemStatus
            ],
          label: key,
        }))
        .filter((option) => option.value <= ACTIVE_ITEM_STATUS_FILTER_MAX),
    ];
  }

  get activeJobs(): ActiveTranscoderJobViewModel[] {
    let result = this.allJobs;

    if (this.selectedItemStatusFilter !== null) {
      result = result.filter((job) =>
        job.itemStatuses.includes(this.selectedItemStatusFilter),
      );
    }

    const nameQuery = this.nameFilter.trim().toLowerCase();
    if (nameQuery) {
      result = result.filter((job) =>
        job.name.toLowerCase().includes(nameQuery),
      );
    }

    return result;
  }

  get groupedJobs(): GroupedMediaJob[] {
    return this.buildGroupedJobs(this.jobsForMediaGrouping);
  }

  private get jobsForMediaGrouping(): ActiveTranscoderJobViewModel[] {
    if (this.selectedItemStatusFilter === null) {
      return this.allJobs;
    }

    return this.allJobs.filter((job) =>
      job.itemStatuses.includes(this.selectedItemStatusFilter),
    );
  }

  get totalMedias(): number {
    return this.groupedJobs.length;
  }

  get totalJobs(): number {
    return this.activeJobs.length;
  }

  setNameFilter(name: string): void {
    this.nameFilter = name;
    this._api.updateDOM();
  }

  toggleNameFilterByBaseName(baseName: string): void {
    if (this.nameFilter === baseName) {
      this.nameFilter = '';
    } else {
      this.nameFilter = baseName;
    }
    this._api.updateDOM();
  }

  isNameFilterActiveForBaseName(baseName: string): boolean {
    return this.nameFilter === baseName;
  }

  setItemStatusFilter(status: EMediaTranscodingItemStatus | null): void {
    this.selectedItemStatusFilter = status;
    this._api.updateDOM();
  }

  public async refreshJobs(): Promise<void> {
    this.allJobs = (await this._api.getJobs()) as ActiveTranscoderJobViewModel[];

    this._api.updateDOM();
  }

  public toggleGroupedJobs(): void {
    this.isGroupedJobsCollapsed = !this.isGroupedJobsCollapsed;
    this._api.updateDOM();
  }

  private buildGroupedJobs(jobs: ActiveTranscoderJobViewModel[]): GroupedMediaJob[] {
    const jobGroups = new Map<string, ActiveTranscoderJobViewModel[]>();

    jobs.forEach((job) => {
      const baseName = this.extractBaseMediaName(job.name);
      if (baseName === '') {
        return;
      }
      if (!jobGroups.has(baseName)) {
        jobGroups.set(baseName, []);
      }
      jobGroups.get(baseName)!.push(job);
    });

    return Array.from(jobGroups.entries()).map(([baseName, groupJobs]) => ({
      baseName,
      jobCount: groupJobs.length,
      jobs: groupJobs,
    }));
  }

  private extractBaseMediaName(jobName: string): string {
    const videoIndex = jobName.indexOf('_VIDEO_');
    const audioIndex = jobName.indexOf('_AUDIO_');

    if (videoIndex !== -1) {
      return jobName.substring(0, videoIndex);
    }
    if (audioIndex !== -1) {
      return jobName.substring(0, audioIndex);
    }
    return '';
  }
}
